import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { makeStyles } from '@material-ui/core';
import {
  CapaPcbServiceType,
  RegisterRfqPcbInput,
  RfqPcbFabricationFileInput
} from '@generated/graphql';
import useRequest from '@hooks/useRequest';
import useQueryString from '@hooks/useQueryString';
import { RequestQueryString } from '@routes/NewRequest/types';
import PCBStepProps, { ProduceRequestForm } from './types';
import {
  CircuitFabricationFileInput,
  CircuitFabricationQuantityInput,
  PublicOpenControl
} from '@routes/NewRequest/FormComponents';
import { CustomizedTypography } from '@components/Customized';
import { useSelector } from 'react-redux';
import { RootState } from '@modules/index';

const PCBProduceStep: React.FC<PCBStepProps> = ({
  setDisabledNextButton,
  nextButtonAction
}) => {
  const currentPcb = useSelector<RootState, RegisterRfqPcbInput>(
    (state) => state.request.pcb ?? { services: [] }
  );
  const fabricationFiles = useSelector<RootState, RfqPcbFabricationFileInput[]>(
    (state) => state.request.pcb?.fabricationFiles ?? []
  );
  const fabricationQuantity = useSelector<RootState, number | null>(
    (state) => state.request.pcb?.fabricationQuantity ?? null
  );
  const publicOpen = useSelector<RootState, boolean>(
    (state) => state.request.publicOpen
  );

  const [isValid, setIsValid] = useState<boolean>(false);
  const { requestContainer } = usePCBRequestStyles();
  const { onUpdateRfqInfo } = useRequest();
  const {
    query: { process }
  } = useQueryString<RequestQueryString>();
  const processes = process?.split(',');
  const hasCircuitDesign = processes?.includes(
    CapaPcbServiceType.CircuitDesign
  );
  const formProps = useForm<ProduceRequestForm>({
    mode: 'onChange',
    defaultValues: {
      publicOpen: publicOpen !== undefined ? publicOpen : true,
      pcb: {
        fabricationQuantity
      }
    }
  });
  const { register, errors, watch } = formProps;
  const watchProduceForm = watch();
  const quantity = watchProduceForm.pcb?.fabricationQuantity;
  const currentFormValue = useRef<ProduceRequestForm>({
    publicOpen: publicOpen !== undefined ? publicOpen : true,
    pcb: {
      fabricationQuantity
    }
  });
  const hasFabrication = process?.includes(
    CapaPcbServiceType.CircuitFabrication
  );
  const fabricationFilesRef = useRef<any[]>([]);

  const onSubmit = useCallback(() => {
    const { publicOpen, pcb } = currentFormValue.current;
    const { fabricationQuantity } = pcb ?? {};
    onUpdateRfqInfo({
      publicOpen: publicOpen,
      pcb: {
        ...currentPcb,
        fabricationFiles: fabricationFilesRef.current,
        services:
          currentPcb.services ??
          processes?.map((service) => ({
            serviceType: service as CapaPcbServiceType
          })) ??
          [],
        fabricationQuantity: fabricationQuantity ?? null
      }
    });
  }, [processes, onUpdateRfqInfo, currentPcb]);

  useEffect(() => {
    currentFormValue.current = {
      publicOpen: watchProduceForm.publicOpen,
      pcb: {
        fabricationQuantity: watchProduceForm.pcb.fabricationQuantity
      }
    };
  }, [watchProduceForm]);

  useEffect(() => {
    nextButtonAction.current.FABRICATION = onSubmit;
  }, [onSubmit, nextButtonAction]);

  useEffect(() => {
    if (!hasCircuitDesign) {
      if (hasFabrication) {
        if (
          fabricationFiles.length > 0 &&
          fabricationFiles.every((file) => {
            return (
              file.fileId && file.fileName && file.quantity && file.thickness
            );
          })
        ) {
          setIsValid(true);
        } else {
          setIsValid(false);
        }
      } else {
        if (fabricationFiles.length > 0) {
          setIsValid(true);
        } else {
          setIsValid(false);
        }
      }
    }

    if (hasCircuitDesign) {
      if (quantity) {
        setIsValid(true);
      } else {
        setIsValid(false);
      }
    }
  }, [fabricationFiles, hasCircuitDesign, quantity, hasFabrication]);

  useEffect(() => {
    if (!setDisabledNextButton) return;
    setDisabledNextButton(!isValid);
  }, [isValid, setDisabledNextButton]);

  useEffect(() => {
    return onSubmit;
    //eslint-disable-next-line
  }, []);

  return (
    <div className={requestContainer}>
      <FormProvider {...formProps}>
        <form>
          {!hasCircuitDesign && (
            <>
              <CircuitFabricationFileInput filesRef={fabricationFilesRef} />
              <PublicOpenControl />
            </>
          )}
          {hasCircuitDesign && (
            <>
              <CircuitFabricationQuantityInput
                error={errors?.pcb?.fabricationQuantity?.message}
                ref={register({ required: '제작 수량을 입력해주세요' })}
              />
              <div className="gerber-description">
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                  gutterBottoms={8}
                >
                  유의사항
                </CustomizedTypography>
                <CustomizedTypography variant="body2" color="textSecondary">
                  PCB 제작을 의뢰하기 위해서는 반드시 '거버파일'로 작업해야
                  합니다. 거버파일에 이상이 있을 경우 품질에 문제가 발생할 수
                  있습니다. 거버파일에 누락된 정보나 오류가 없는지 꼼꼼히
                  확인해주세요.
                </CustomizedTypography>
              </div>
            </>
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default PCBProduceStep;

const usePCBRequestStyles = makeStyles((theme) => ({
  requestContainer: {
    width: '100%',
    maxWidth: '464px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '72px',
    '& .gerber-description': {
      marginTop: 40,
      '& > p': {
        wordBreak: 'break-all'
      }
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: 0
    }
  }
}));
